import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  OnInit,
  TemplateRef,
  ViewChild,
  inject,
} from '@angular/core';
import { SepTableComponent } from '../../../../../../shared/components/sep-table/sep-table.component';
import { TranslocoDirective } from '@jsverse/transloco';
import {
  TextButtonComponent,
  ButtonColor,
} from '../../../../../../shared/components/text-button/text-button.component';
import { catchError, EMPTY, map, Observable } from 'rxjs';
import { OrganizationDTO } from '../../models/OrganizationDTO';
import { OrganizationService } from '../../services/organization.service';
import { AsyncPipe } from '@angular/common';
import { SnackbarService } from '../../../../../../core/services/snackbar.service';
import { SepAdminService } from '../../../services/sep-admin.service';
import { LanguageService } from '../../../../../../core/services/language.service';
import { Column } from '../../../../../../shared/components/sep-table/Column';
import { CustomPortalError } from '../../../../../../core/errors/models/CustomPortalError';
import { MatDialog } from '@angular/material/dialog';
import { DialogModel } from '../../../../../../shared/components/dialog/dialog';
import {
  AbstractControl,
  FormControl,
  ReactiveFormsModule,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';

@Component({
  selector: 'app-organizations-table',
  imports: [
    SepTableComponent,
    TextButtonComponent,
    AsyncPipe,
    TranslocoDirective,
    ReactiveFormsModule,
  ],
  templateUrl: './organizations-table.component.html',
})
export class OrganizationsTableComponent implements OnInit, AfterViewInit {
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly organizationService = inject(OrganizationService);
  private readonly snackbarService = inject(SnackbarService);
  private readonly sepAdminService = inject(SepAdminService);
  private readonly dialog = inject(MatDialog);
  private readonly languageService = inject(LanguageService);

  public readonly buttonColor = ButtonColor;
  @ViewChild('organization', { static: true }) organization!: TemplateRef<{
    $implicit: string;
  }>;

  @ViewChild('deleteDialogContent', { static: true })
  deleteDialogContent!: TemplateRef<Element>;

  columns: Array<Column<OrganizationDTO, string | { $implicit: string }>> = [
    {
      headerName: this.languageService.translateOnRuntime(
        'admin.organizations.organizationColumnHeader',
      ),
      renderComponent: undefined,
      key: 'name',
    },
  ];
  organizations$: Observable<OrganizationDTO[]>;

  confirmationControl: FormControl<string> = new FormControl('', {
    nonNullable: true,
    validators: [Validators.required],
  });
  targetOrgName?: string;
  reminderStripeText?: string;
  reminderUnifiedText?: string;

  ngOnInit() {
    this.loadOrganizations();
  }

  //reuse for initial load and post-delete refresh
  private loadOrganizations(): void {
    this.organizations$ = this.organizationService.getOrganizations().pipe(
      map((response) => response.organizations),
      catchError((error: CustomPortalError) => {
        this.snackbarService.error(error.errorType);
        return EMPTY;
      }),
    );
  }

  ngAfterViewInit(): void {
    this.columns[0].renderComponent = this.organization;
    this.cdr.detectChanges();
  }

  handleTakeOverButtonClick(organization: OrganizationDTO): void {
    this.sepAdminService.startImpersonation(organization);
  }

  openDeleteDialog(org: OrganizationDTO): void {
    this.targetOrgName = org.name;
    this.reminderStripeText = this.languageService.translateOnRuntime(
      'admin.organizations.deleteDialog.reminderStripe',
      { subscriptionId: org.subscriptions[0].externalSubscriptionId },
    );

    this.reminderUnifiedText = this.languageService.translateOnRuntime(
      'admin.organizations.deleteDialog.reminderUnified',
    );

    this.confirmationControl.reset('');
    this.confirmationControl.setValidators([
      Validators.required,
      this.matchNameValidator(org.name),
    ]);
    this.confirmationControl.updateValueAndValidity({ emitEvent: false });

    const dialogModel = new DialogModel(this.dialog);
    dialogModel.headline = this.languageService.translateOnRuntime(
      'admin.organizations.deleteDialog.headline',
    );
    dialogModel.closable = true;
    dialogModel.contentTemplate = this.deleteDialogContent;

    const buttonRow = {
      confirmButtonText: this.languageService.translateOnRuntime(
        'admin.organizations.deleteDialog.confirmButtonText',
      ),
      declineButtonText: this.languageService.translateOnRuntime(
        'admin.organizations.deleteDialog.cancelButtonText',
      ),
      confirmButtonDisabled: true,
      confirmButtonLoading: false,
      confirmButtonAction: () => {
        this.confirmDelete(org, dialogModel, buttonRow);
      },
    };
    dialogModel.buttonRowData = buttonRow;

    this.confirmationControl.valueChanges.subscribe(() => {
      buttonRow.confirmButtonDisabled = this.confirmationControl.invalid;
    });

    dialogModel.openDialog();
  }

  private matchNameValidator(expected: string): ValidatorFn {
    return (control: AbstractControl<string>): ValidationErrors | null =>
      control.value.trim() === expected ? null : { nameMismatch: true };
  }

  private confirmDelete(
    org: OrganizationDTO,
    dialogModel: DialogModel,
    buttonRow: { confirmButtonLoading: boolean },
  ): void {
    buttonRow.confirmButtonLoading = true;

    this.organizationService.deleteTenant(org.tenant.id).subscribe({
      next: () => {
        buttonRow.confirmButtonLoading = false;
        dialogModel.closeDialog();
        this.snackbarService.success(
          'admin.organizations.successfulDeleteSnackbar',
          { name: org.name },
        );
        this.loadOrganizations();
      },
      error: (error: CustomPortalError) => {
        buttonRow.confirmButtonLoading = false;
        console.error('Deletion failed:', error.errorDetails);
        this.snackbarService.errorMessage(
          this.languageService.translateOnRuntime(
            'admin.organizations.deleteDialog.error',
          ),
        );
      },
    });
  }
}
